import { useCallback, useMemo, useState } from "react";

import { type AWSNodeConfigApiParameters, type CloudFlowNodeType } from "@doitintl/cmp-models";
import set from "lodash/set";

import { cloudflowTexts } from "../../../../../../assets/texts";
import { GenericApiActionParametersForm } from "../../../ApiActionParametersForm/ApiActionParametersForm";
import { getInitialValueForModel } from "../../../ApiActionParametersForm/useApiActionParametersSchema";
import { useUnwrappedApiActionModel } from "../../../Common/hooks/useUnwrappedApiActionModel";
import { useNodeConfigurationContext } from "../../NodeConfigurationContext";
import { useErrorUpdates } from "../hooks";
import { type WaiterAutocompleteValue } from "./useAWSWaiters";

export const WaiterParameters = ({
  parameters,
  selectedWaiter,
}: {
  parameters: AWSNodeConfigApiParameters;
  selectedWaiter: WaiterAutocompleteValue | null;
}) => {
  const { updateNode } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const [isWaiterParametersValid, setIsWaiterParametersValid] = useState(Boolean(selectedWaiter?.inputModel?.id));
  const { model: waiterInputModel } = useUnwrappedApiActionModel(parameters.operation, selectedWaiter?.inputModel?.id);

  useErrorUpdates<CloudFlowNodeType.ACTION>({
    isValid: isWaiterParametersValid,
    errorKey: "waiter_param_error",
    errorMessage: cloudflowTexts.STATUS.WAITER_REUIRED_FIELDS_ERROR,
  });

  const onWaiterParametersChange = useCallback(
    (values) => {
      updateNode((prev) => set(prev, "parameters.waiterConfiguration.parameters", values));
    },
    [updateNode]
  );

  const initialValues = useMemo<unknown>(() => {
    if (!waiterInputModel) {
      return null;
    }
    return parameters.waiterConfiguration?.parameters
      ? parameters.waiterConfiguration?.parameters
      : getInitialValueForModel(waiterInputModel);
  }, [parameters.waiterConfiguration, waiterInputModel]);

  if (!waiterInputModel || !initialValues || !selectedWaiter) {
    return null;
  }

  return (
    <GenericApiActionParametersForm
      key={selectedWaiter.id}
      values={initialValues}
      inputModel={waiterInputModel}
      onValidityChange={setIsWaiterParametersValid}
      onValuesChange={onWaiterParametersChange}
    />
  );
};
